import { formatCorrectPercent } from './correct-percent';

export type SortMode = 'default' | 'weakness';

export interface ChapterCounts {
  correct_question_count: number | null | undefined;
  wrong_question_count: number | null | undefined;
}

export interface ChapterStats extends ChapterCounts {
  id: number;
}

/** Number of answered questions (correct + wrong); unanswered ones are ignored, like in the % formula. */
export function answeredCount(row: ChapterCounts): number {
  return count(row.correct_question_count) + count(row.wrong_question_count);
}

/**
 * Share correct among answered questions (0..1) with the same definition as
 * formatCorrectPercent (Richtig / (Richtig + Falsch)); null when nothing was answered.
 * The displayed percentage is not changed by this module.
 */
export function correctRatio(row: ChapterCounts): number | null {
  const answered = answeredCount(row);
  return answered === 0 ? null : count(row.correct_question_count) / answered;
}

/**
 * Stable ascending sort by % correct. Chapters without any answered question
 * (shown as an en dash) go to the end, in their original order.
 */
export function sortByWeakness<T extends ChapterStats>(rows: T[]): T[] {
  return rows
    .map((row, index) => ({ row, index, ratio: correctRatio(row) }))
    .sort((a, b) => {
      if (a.ratio === null && b.ratio === null) { return a.index - b.index; }
      if (a.ratio === null) { return 1; }
      if (b.ratio === null) { return -1; }
      return a.ratio - b.ratio || a.index - b.index;
    })
    .map(item => item.row);
}

/** Ids of the `limit` weakest chapters that have at least one answered question. */
export function weakestIds<T extends ChapterStats>(rows: T[], limit = 3): number[] {
  return sortByWeakness(rows)
    .filter(row => answeredCount(row) > 0)
    .slice(0, limit)
    .map(row => row.id);
}

/** Convenience wrapper so templates/tests can compare against the displayed text. */
export function displayedPercent(row: ChapterCounts): string {
  return formatCorrectPercent(row.correct_question_count, row.wrong_question_count);
}

function count(value: number | null | undefined): number {
  const n = Number(value);
  return isFinite(n) && n > 0 ? n : 0;
}
